import { useEffect } from 'react';
import { isAgentDynamicWiFiEnabled, isEdgeSensor } from '../../../agents/AgentHelper';
import useAgentsContext from './AgentsContextProvider';
import { AGENT_IDS_FIELD_NAME, WIRELESS } from '../../ServiceTestsConstants';

const isWireless = (adapterType) => WIRELESS === adapterType;

/**
 * Agent API fetching, cancel logic, client-side filtering, and ID reconciliation.
 *
 * Reloads the agent list when adapterType changes. Applies client-side post-filtering
 * for WiFi test types matching portal/ui testsDetails.controller.js queryAgents().
 * Reconciles agents.ids against the fresh response to remove stale IDs.
 *
 * @state-ownership
 * - Reads from RHF: agents.ids via getValuesRef (for reconciliation)
 * - Writes to RHF: agents.ids via setValueRef (reconciliation)
 * - Params: { adapterType, testTypeRef, getValuesRef, setValueRef, onBeforeFetch }
 */
export function useFetchAgents({ adapterType, testTypeRef, getValuesRef, setValueRef, onBeforeFetch }) {
    const { wirelessAgents, defaultAgents, setAvailableAgents } = useAgentsContext();
    // Reload agents on adapterType change.
    // All other dependencies accessed via .current on refs — only adapterType triggers re-fetch.
    useEffect(() => {
        // Save/restore selection history synchronously before async fetch
        onBeforeFetch(adapterType);
        let agents;
        if (isWireless(adapterType)) {
            agents = wirelessAgents;
        } else {
            agents = defaultAgents;
        }

        // Client-side post-filtering matching portal/ui testsDetails.controller.js queryAgents().
        // The server-side picker already filters by dynamicWifi/wifi params,
        // but portal/ui additionally filters client-side for wifiConnectTest types.
        if (testTypeRef.current?.wifiConnectTest === true) {
            agents = agents.filter((agent) => isAgentDynamicWiFiEnabled(agent));
            if (testTypeRef.current?.group === 'wifiConnectMultiTest') {
                agents = agents.filter((agent) => isEdgeSensor(agent));
            }
        }

        setAvailableAgents(agents);

        // Reconcile agents.ids against the fresh picker response.
        // Mirrors portal/ui testAgentsPicker.controller.js updateIdsArray():
        // clears ids, then rebuilds from only agents present in $scope.list.
        const currentIds = getValuesRef.current(AGENT_IDS_FIELD_NAME) || [];
        if (currentIds.length > 0) {
            const availableIdSet = new Set(agents.map((a) => a._id));
            const validIds = currentIds.filter((id) => availableIdSet.has(id));
            if (validIds.length !== currentIds.length) {
                setValueRef.current(AGENT_IDS_FIELD_NAME, validIds);
            }
        }
    }, [
        adapterType,
        defaultAgents,
        getValuesRef,
        onBeforeFetch,
        setAvailableAgents,
        setValueRef,
        testTypeRef,
        wirelessAgents,
    ]);
}
